import { streamChat, streamToolInput, respondToolApproval, respondToolApprovalBatch } from '../api/chat.js'
import { uploadFile } from '../api/files.js'
import { useChatStore } from '../stores/chat.js'
import { useSessionsStore } from '../stores/sessions.js'
import { useCreditsStore } from '../stores/credits.js'
import { useRPilotConfig } from './useRPilotConfig.js'
import { useI18n } from './useI18n.js'

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function useChat() {
  const chatStore = useChatStore()
  const sessionsStore = useSessionsStore()
  const creditsStore = useCreditsStore()
  const cfg = useRPilotConfig()
  const { t } = useI18n()

  async function sendMessage(text, files = []) {
    const hasText = text.trim().length > 0
    const hasFiles = files.length > 0
    // isBusy also covers a turn paused on a frontend tool: a message sent
    // mid-pause would land on the backend behind an unanswered tool call and
    // corrupt the session's message order.
    if ((!hasText && !hasFiles) || chatStore.isBusy) return

    // Partition attachments by handling path: images ride inline as base64
    // image_url content parts (unchanged); spreadsheets upload to the server
    // first and contribute a metadata stub to the outgoing text — the file bytes
    // never enter message content.
    const imageFiles = []
    const spreadsheetFiles = []
    for (const file of files) {
      if (file.type && file.type.startsWith('image/')) imageFiles.push(file)
      else spreadsheetFiles.push(file)
    }
    const hasImages = imageFiles.length > 0

    chatStore.isStreaming = true
    // Held on the chat store (not this composable instance) so a widget
    // remounted mid-stream can still stop it — see the store comment. Keep a
    // local handle too: the finally below must only tear down streaming state
    // if this stream is still the active one (see comment there).
    const controller = new AbortController()
    chatStore.abortController = controller

    try {
      // Ensure a session exists before anything else — uploads need the session
      // id, and rdrive-mcp requires an explicit session created with scoped
      // credentials before any chat request. The backend picks the model from the
      // agent profile; we don't supply one. Hoisted above content assembly so
      // spreadsheet uploads below can reference the session.
      if (!sessionsStore.activeSessionId) {
        await sessionsStore.create(cfg?.sessionCreateOptions || {})
      }

      // Upload spreadsheet attachments and append each returned stub to the
      // outgoing message text (blank-line separated). A failed upload aborts the
      // whole send — no partial message — with an error bubble.
      let messageText = hasText ? text.trim() : ''
      for (const file of spreadsheetFiles) {
        let uploaded
        try {
          uploaded = await uploadFile(sessionsStore.activeSessionId, file)
        } catch (e) {
          if (e.name === 'AbortError') return
          chatStore.addError(`${t('chat.uploadFailed')}: ${e.message}`)
          return
        }
        if (uploaded?.stub) {
          messageText = messageText ? `${messageText}\n\n${uploaded.stub}` : uploaded.stub
        }
      }

      // Images become base64 image_url content parts alongside the (stub-augmented)
      // text; a spreadsheet-only send stays a plain string.
      let content
      if (hasImages) {
        const parts = []
        if (messageText) parts.push({ type: 'text', text: messageText })
        for (const file of imageFiles) {
          const dataUrl = await fileToBase64(file)
          parts.push({ type: 'image_url', image_url: { url: dataUrl } })
        }
        content = parts
      } else {
        content = messageText
      }

      chatStore.addUserMessage(content)

      // One-shot skill directive: consume the dropdown pick (or queued "No
      // skill" deactivation) so it rides on exactly this request. The
      // skill_activated / skill_deactivated SSE event (emitted before any
      // content) confirms it and updates the indicator. If the request
      // fails (e.g. 404 unknown skill, 400 empty tool intersection — both
      // pre-SSE) the directive is dropped with it; the user re-picks rather
      // than the widget silently retrying a bad name every turn.
      const { skillName, deactivate } = chatStore.takePendingSkillDirective()

      const { sessionId } = await streamChat({
        sessionId: sessionsStore.activeSessionId,
        message: messageText,
        content: hasImages ? content : undefined,
        skillName: skillName || undefined,
        deactivateSkill: deactivate || undefined,
        signal: controller.signal,
        onEvent: handleEvent,
      })

      // If the backend created a new session implicitly (X-Session-Id header), track it
      if (sessionId && sessionId !== sessionsStore.activeSessionId) {
        sessionsStore.addImplicit({
          id: sessionId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        chatStore.addError(e.message)
      }
    } finally {
      // A frontend tool that responds synchronously resumes the turn on a NEW
      // stream (resumeWithToolInput installs its own controller) before this
      // stream's close frame arrives over the network. Only tear down the
      // shared streaming state if this stream is still the active one —
      // otherwise we'd clobber the resumed turn's isStreaming flag (killing
      // the agent loader / shimmer) and null out its abort controller
      // (breaking Stop).
      if (chatStore.abortController === controller) {
        chatStore.finalizeContent()
        chatStore.isStreaming = false
        chatStore.abortController = null
      }
    }
  }

  function handleEvent(event) {
    switch (event.type) {
      case 'analysis':
        chatStore.appendAnalysis(event.content)
        break
      case 'thinking':
        // Reasoning models (e.g. qwen3.6) stream their chain-of-thought as
        // `thinking` deltas. Same UI channel as `analysis` — accumulate into
        // the reasoning buffer so it renders in the reasoning panel.
        chatStore.appendAnalysis(event.content)
        break
      case 'content':
        chatStore.appendContent(event.content)
        break
      case 'tool_call':
        chatStore.addToolCall(event)
        break
      case 'sync_progress':
        chatStore.setSyncProgress(event)
        break
      case 'tool_result':
        chatStore.setToolResult(event)
        break
      case 'tool_error':
        chatStore.setToolError(event)
        break
      case 'frontend_tool_call':
        chatStore.setPendingFrontendToolCall(event)
        break
      case 'tool_approval_batch':
        // Envelope emitted just before the per-call tool_approval_required
        // events when a round gates 2+ mutations. Opens one batch card that the
        // following per-call events (matched by batchId) fold themselves into.
        chatStore.addPendingApprovalBatch(event)
        break
      case 'tool_approval_required':
        // Backend is holding the stream open awaiting a human decision. Push an
        // approval card into the transcript — or, when the event carries a
        // batchId matching an open envelope, fold it into that batch card. The
        // user answers via approveTool / approveBatch, and
        // `tool_approval_resolved` confirms below.
        chatStore.addPendingApproval(event)
        break
      case 'tool_approval_resolved':
        chatStore.resolveApproval(event.toolCallId, event.decision)
        break
      case 'skill_activated':
        // Emitted for both UI-driven activation (the one-shot `skillName`
        // request field) and the model's own activation tool call.
        chatStore.setSkillActivated(event.skillName)
        break
      case 'skill_deactivated':
        chatStore.setSkillDeactivated()
        break
      case 'error':
        // Credit rejection is a friendly, expected state — not a raw error.
        // Intercept it before the generic error bubble: render the inline
        // exhausted notice (with a contact-sales dialog) and disable the
        // composer. Also refresh credits so the next widget mount boots
        // straight into the pre-flight gate.
        if (event.code === 'no_pool' || event.code === 'insufficient_credits') {
          chatStore.addCreditExhausted()
          creditsStore.load()
        } else {
          chatStore.addError(event.error)
        }
        break
      case 'link':
        chatStore.appendLink(event)
        break
      case 'content_done':
        chatStore.finalizeContent('done')
        break
      case 'done':
        chatStore.finalizeContent()
        break
    }
  }

  /** Resume the conversation after a host-supplied frontend tool result. */
  async function resumeWithToolInput(sessionId, toolCallId, result) {
    chatStore.isStreaming = true
    const controller = new AbortController()
    chatStore.abortController = controller

    try {
      await streamToolInput({
        sessionId,
        toolCallId,
        result,
        signal: controller.signal,
        onEvent: handleEvent,
      })
    } catch (e) {
      if (e.name !== 'AbortError') {
        chatStore.addError(e.message)
      }
    } finally {
      // Same takeover guard as sendMessage: a resumed turn can itself hit
      // another synchronous frontend tool, chaining a further resume before
      // this stream's close lands.
      if (chatStore.abortController === controller) {
        chatStore.finalizeContent()
        chatStore.isStreaming = false
        chatStore.abortController = null
      }
    }
  }

  /**
   * Answer a human-in-the-loop tool-approval request. Optimistically settle the
   * card locally, then POST the decision on the side channel — the backend
   * confirms by emitting `tool_approval_resolved` on the still-open stream
   * (which overwrites the local status, harmlessly, since they agree). The
   * stream stays open throughout, so isStreaming remains true; hitting Stop
   * aborts the fetch, which the backend treats as a deny (the escape hatch).
   */
  async function approveTool(toolCallId, approved) {
    chatStore.resolveApproval(toolCallId, approved ? 'approved' : 'denied')
    const sessionId = sessionsStore.activeSessionId
    if (!sessionId) return
    try {
      await respondToolApproval({ sessionId, toolCallId, approved })
    } catch (e) {
      chatStore.addError(e.message)
    }
  }

  /**
   * Answer a grouped batch approval in one POST. Mirrors approveTool but over a
   * list of per-item decisions ([{ toolCallId, approved }]): optimistically
   * settle every item so the card collapses immediately, then POST the bulk
   * decision. The server's `tool_approval_resolved` events overwrite the local
   * status harmlessly (they agree). Unlike the single card, a batch is a costly
   * decision to lose to a transient failure, so on error we roll the optimistic
   * settle back to pending — re-enabling the card's buttons for a retry — since
   * the gates are still live server-side (the per-item POST fallback also works).
   */
  async function approveBatch(batchId, decisions) {
    for (const d of decisions) {
      chatStore.resolveApproval(d.toolCallId, d.approved ? 'approved' : 'denied')
    }
    const sessionId = sessionsStore.activeSessionId
    if (!sessionId) return
    try {
      await respondToolApprovalBatch({ sessionId, decisions })
    } catch (e) {
      chatStore.resetBatchApproval(batchId, decisions.map(d => d.toolCallId))
      chatStore.addError(e.message)
    }
  }

  function stopStreaming() {
    // A turn paused on a frontend tool has no open stream to abort. Stop
    // answers the pending call with a cancel error instead (the same result a
    // host sends when the user dismisses its dialog), so the backend records
    // the cancellation and the turn resumes — the model wraps up, and a second
    // Stop aborts that stream as usual. Without a canceller (no ChatContainer
    // ever saw the call) just drop it locally so the widget isn't stuck busy.
    if (chatStore.pendingFrontendToolCall) {
      const cancel = chatStore.frontendToolCanceller
      if (cancel) cancel()
      else chatStore.clearPendingFrontendToolCall()
      return
    }
    if (chatStore.abortController) {
      chatStore.abortController.abort()
    }
  }

  return { sendMessage, resumeWithToolInput, approveTool, approveBatch, stopStreaming }
}
